import { useState, useCallback, useMemo, useEffect } from 'react'
import { AnimatePresence, motion, type Variants, type Transition } from 'framer-motion'
import { useAtomValue, useSetAtom } from 'jotai'
import { Trash2, X } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import {
  historySelectedCountAtom,
  historySelectedBreakdownAtom,
  clearHistorySelectionAtom,
} from '@renderer/overlay/Sidepanel/stores/history-select'
import { useHistoryBulkDelete } from '../use-history-bulk-delete'

const actionsBarVariants: Variants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
}

const actionsBarTransition: Transition = { duration: 0.15 }

export function HistoryActionsBar() {
  const count = useAtomValue(historySelectedCountAtom)
  const breakdown = useAtomValue(historySelectedBreakdownAtom)
  const clearSelection = useSetAtom(clearHistorySelectionAtom)
  const { handleDelete, isDeleting } = useHistoryBulkDelete()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  useEffect(() => {
    if (count === 0) {
      setIsConfirmingDelete(false)
    }
  }, [count])

  const onClear = useCallback(() => {
    clearSelection()
  }, [clearSelection])

  const onDelete = useCallback(() => {
    setIsConfirmingDelete(true)
  }, [])

  const onConfirmDelete = useCallback(async () => {
    await handleDelete()
    setIsConfirmingDelete(false)
  }, [handleDelete])

  const onCancelDelete = useCallback(() => {
    setIsConfirmingDelete(false)
  }, [])

  const tooltipContent = useMemo(() => {
    const lines: Array<string> = []
    if (breakdown.chats > 0) {
      lines.push(`${breakdown.chats} ${breakdown.chats === 1 ? 'chat' : 'chats'}`)
    }
    if (breakdown.audioNotes > 0) {
      lines.push(`${breakdown.audioNotes} ${breakdown.audioNotes === 1 ? 'audio note' : 'audio notes'}`)
    }
    if (breakdown.actions > 0) {
      lines.push(`${breakdown.actions} ${breakdown.actions === 1 ? 'action' : 'actions'}`)
    }
    return (
      <div className="flex flex-col gap-1">
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </div>
    )
  }, [breakdown])

  if (count === 0) return null

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isConfirmingDelete ? (
        <motion.div
          key="confirm"
          variants={actionsBarVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={actionsBarTransition}
          className="flex items-center gap-2"
        >
          <Button variant="ghost" onClick={onCancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="primary" tone="danger" onClick={onConfirmDelete} loading={isDeleting}>
            <Trash2 />
            Delete {count} {count === 1 ? 'item' : 'items'}
          </Button>
        </motion.div>
      ) : (
        <motion.div
          key="actions"
          variants={actionsBarVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={actionsBarTransition}
          className="flex items-center gap-2"
        >
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button variant="ghost" onClick={onClear}>
                  <X />
                  {count} selected
                </Button>
              }
            />
            <Tooltip.Content className="whitespace-normal">{tooltipContent}</Tooltip.Content>
          </Tooltip>
          <div className="h-5 border-l" />
          <Button variant="primary" tone="danger" onClick={onDelete}>
            <Trash2 />
            Delete
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
